import { useState, useEffect } from 'react';
import { X, Share, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { triggerHaptic } from '@/lib/haptics';

const IOS_PROMPT_DISMISSED_KEY = 'parkrow_ios_install_dismissed';
const IOS_PROMPT_DELAY_MS = 5000; // 5 seconds after user interaction

export function IOSInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    // Only show on iOS Safari (not in standalone mode)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isStandalone = (window.navigator as any).standalone === true || 
      window.matchMedia('(display-mode: standalone)').matches;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (!isIOS || isStandalone || !isSafari) return;

    // Check if already dismissed
    const dismissed = localStorage.getItem(IOS_PROMPT_DISMISSED_KEY);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      // Don't show again for 14 days
      if (Date.now() - dismissedAt < 14 * 24 * 60 * 60 * 1000) return;
    }

    // Listen for user interaction
    const handleInteraction = () => {
      if (!hasInteracted) {
        setHasInteracted(true);
      }
    };

    window.addEventListener('click', handleInteraction, { once: true });
    window.addEventListener('touchstart', handleInteraction, { once: true });
    window.addEventListener('scroll', handleInteraction, { once: true });

    return () => {
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
    };
  }, [hasInteracted]);

  useEffect(() => {
    if (!hasInteracted) return;

    const timer = setTimeout(() => {
      setShowPrompt(true);
      triggerHaptic('light');
    }, IOS_PROMPT_DELAY_MS);

    return () => clearTimeout(timer);
  }, [hasInteracted]);

  const handleDismiss = () => {
    triggerHaptic('light');
    localStorage.setItem(IOS_PROMPT_DISMISSED_KEY, Date.now().toString());
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-end justify-center p-4 safe-bottom">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={handleDismiss}
      />
      
      {/* Prompt Card */}
      <div className="relative bg-card border border-border rounded-2xl p-6 w-full max-w-sm shadow-2xl animate-slide-up">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-2 text-muted-foreground hover:text-foreground transition-colors touch-target tap-highlight-none"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
            <img 
              src="/apple-touch-icon.png" 
              alt="Park Row" 
              className="w-10 h-10 rounded-lg"
            />
          </div>
          <div>
            <h3 className="font-bold text-lg text-foreground">Install Park Row</h3>
            <p className="text-sm text-muted-foreground">Add to your home screen</p>
          </div>
        </div>

        {/* Instructions */}
        <div className="space-y-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-bold text-sm">1</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-foreground">
              <span>Tap the</span>
              <Share className="h-5 w-5 text-primary" />
              <span className="font-medium">Share</span>
              <span>button below</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-bold text-sm">2</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-foreground">
              <span>Scroll and tap</span>
              <span className="inline-flex items-center gap-1 bg-muted px-2 py-1 rounded font-medium">
                <Plus className="h-4 w-4" />
                Add to Home Screen
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-bold text-sm">3</span>
            </div>
            <div className="text-sm text-foreground">
              <span>Tap </span>
              <span className="font-medium text-primary">Add</span>
              <span> to confirm</span>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="bg-muted/50 rounded-xl p-4 mb-4">
          <p className="text-xs text-muted-foreground">
            Get instant access to tour dates, shop merch, and never miss a show with push notifications.
          </p>
        </div>

        {/* Dismiss button */}
        <Button
          onClick={handleDismiss}
          variant="ghost"
          className="w-full text-muted-foreground hover:text-foreground"
        >
          Maybe Later
        </Button>
      </div>
    </div>
  );
}
